import React, { PropsWithChildren, ReactNode, Fragment, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Progress } from "reactstrap";

import { FieldProps } from "formik";
import isEmpty from "lodash/isEmpty";

import { Preloader } from "components";

import { StyledContainer, StyledContent } from "./styled";

type TUpload = PropsWithChildren<ReactNode> &
  FieldProps & {
    accept: string;
    loading?: boolean;
    loadingProgress?: number;
    withProgress?: boolean;
    uploadText: string;
    changeText: string;
    defaultValue?: string;
    alwaysShowButtons?: boolean;
    renderFile?: (file: File) => ReactNode;
    onUpload?(file: File): void;
  };

const renderImage = (file: File) => (
  <span className="dz-preview">
    <div className="dz-image mb-1">
      <img style={{ maxWidth: "100%", maxHeight: 400 }} src={URL.createObjectURL(file)} />
    </div>

    <span className="dz-details">
      <span className="dz-size">
        <span data-dz-size="">
          <strong>{(file.size / 1024).toFixed(2)}</strong> KB
        </span>
      </span>

      <div className="dz-filename">
        <span data-dz-name="">{file.name}</span>
      </div>
    </span>
  </span>
);

const Upload = ({
  accept = "image/*",
  children,
  loading = false,
  loadingProgress = 0,
  withProgress = false,
  uploadText,
  changeText,
  defaultValue,
  alwaysShowButtons,
  field,
  form,
  renderFile = renderImage,
  onUpload = () => {}
}: TUpload) => {
  const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept,
    multiple: false,
    disabled: loading
  });

  const error = form.errors[field.name];
  const touched = form.touched[field.name];

  return useMemo(() => {
    return (
      <StyledContainer valid={field.value && !error} invalid={error} {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps({ name: field.name, type: "file" })} />
        <StyledContent visible={!acceptedFiles.length} onClick={open}>
          {children}
        </StyledContent>

        <Preloader loading={!withProgress && loading}>
          {withProgress && loading ? (
            <Progress className="mt-2 mb-2" striped value={loadingProgress} max={100} />
          ) : (
            !isEmpty(acceptedFiles) && (
              <Fragment>
                {acceptedFiles.map(file => renderFile(file))}
                {(!field.value || alwaysShowButtons) && (
                  <div className="mt-1">
                    <Button
                      type="button"
                      className="mr-1"
                      size="sm"
                      color="info"
                      onClick={() => onUpload(acceptedFiles[0])}>
                      {uploadText}
                    </Button>
                    <Button type="reset" size="sm" color="danger" onClick={open}>
                      {changeText}
                    </Button>
                  </div>
                )}
              </Fragment>
            )
          )}
        </Preloader>
      </StyledContainer>
    );
  }, [acceptedFiles, loading, field.value, error, touched, loadingProgress, defaultValue]);
};

export default Upload;
